import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as openpgp from 'openpgp/lightweight';

const PGPManager = () => {
  const [keys, setKeys] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  useEffect(() => {
    fetchKeys();
    fetchUserEmail();
  }, []);

  const fetchUserEmail = async () => {
    try {
      const response = await axios.get('/api/subscriptions/current');
      if (response.data?.user?.username) {
        setUserEmail(`${response.data.user.username}@addisonelectronique.com`);
      }
    } catch (err) {
      // Use placeholder
      setUserEmail('user@addisonelectronique.com');
    }
  };

  const fetchKeys = async () => {
    try {
      const response = await axios.get('/api/pgp/keys');
      setKeys(response.data.keys || []);
    } catch (err) {
      console.error('Failed to fetch keys:', err);
    }
  };

  const generateKeyPair = async () => {
    if (!keyName || !userEmail) {
      setError('Key name and email are required');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const { privateKey: privKey, publicKey: pubKey } = await openpgp.generateKey({
        type: 'ecc',
        curve: 'curve25519',
        userIDs: [{ name: keyName, email: userEmail }],
        passphrase: passphrase || undefined,
        format: 'armored'
      });

      setPublicKey(pubKey);
      setPrivateKey(privKey);
      setGeneratedKey({
        publicKey: pubKey,
        privateKey: privKey,
        fingerprint: pubKey.match(/[A-F0-9]{40}/)?.[0] || 'Unknown'
      });
      
    } catch (err) {
      console.error('Key generation error:', err);
      setError('Failed to generate key: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveGeneratedKey = async () => {
    if (!generatedKey) return;
    
    setLoading(true);
    try {
      await axios.post('/api/pgp/keys', {
        key_name: keyName,
        public_key: publicKey,
        private_key: privateKey
      });
      
      // Clear form
      setKeyName('');
      setPublicKey('');
      setPrivateKey('');
      setPassphrase('');
      setGeneratedKey(null);
      setShowGenerateForm(false);
      fetchKeys();
      
      alert('✅ PGP key generated and saved successfully!\n\n⚠️ IMPORTANT: Save your private key somewhere safe. It cannot be recovered if lost!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save key');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/pgp/keys', {
        key_name: keyName,
        public_key: publicKey,
        private_key: privateKey
      });
      
      setKeyName('');
      setPublicKey('');
      setPrivateKey('');
      setShowAddForm(false);
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add key');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (id) => {
    if (!window.confirm('Delete this PGP key?')) return;
    
    try {
      await axios.delete(`/api/pgp/keys/${id}`);
      fetchKeys();
    } catch (err) {
      alert('Failed to delete key');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`/api/pgp/keys/${id}/default`);
      fetchKeys();
    } catch (err) {
      alert('Failed to set default key');
    }
  };

  const downloadPrivateKey = () => {
    const blob = new Blob([privateKey], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `addison-private-key-${keyName.replace(/\s+/g, '-')}.asc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
          🔐 PGP Key Manager
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Generate and manage OpenPGP keys for secure email encryption
        </p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <button
          className="btn btn-primary"
          onClick={() => {
            setShowGenerateForm(!showGenerateForm);
            setShowAddForm(false);
            setGeneratedKey(null);
          }}
        >
          {showGenerateForm ? '✕ Cancel' : '⚡ Generate New Key'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => {
            setShowAddForm(!showAddForm);
            setShowGenerateForm(false);
          }}
        >
          {showAddForm ? '✕ Cancel' : '📥 Import Key'}
        </button>
      </div>

      {/* Generate Key Form */}
      {showGenerateForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '8px' }}>⚡ Generate New PGP Key Pair</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: '20px', fontSize: '14px' }}>
            Creates a new ECC (Curve25519) key pair for signing and encryption
          </p>
          
          {!generatedKey ? (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                  Key Name *
                </label>
                <input
                  type="text"
                  className="input"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., Personal Key 2025"
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  className="input"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="your@addisonelectronique.com"
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                  Passphrase (optional but recommended)
                </label>
                <input
                  type="password"
                  className="input"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Protect your private key"
                />
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  You'll need this passphrase to use the private key
                </p>
              </div>

              <button 
                className="btn btn-primary" 
                onClick={generateKeyPair}
                disabled={generating || !keyName || !userEmail}
              >
                {generating ? '⏳ Generating... (this may take a moment)' : '⚡ Generate Key Pair'}
              </button>
            </>
          ) : (
            <div style={{ animation: 'fadeIn 0.3s' }}>
              <div className="alert alert-success" style={{ marginBottom: '20px' }}>
                ✅ Key generated successfully! Save your private key now.
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                  Public Key (safe to share)
                </label>
                <textarea
                  className="input"
                  value={publicKey}
                  readOnly
                  rows="4"
                  style={{ fontFamily: 'monospace', fontSize: '11px' }}
                />
                <button 
                  className="btn btn-ghost" 
                  style={{ marginTop: '8px', fontSize: '12px' }}
                  onClick={() => copyToClipboard(publicKey)}
                >
                  📋 Copy Public Key
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: '#DC2626', fontWeight: '600' }}>
                    🔒 Private Key (KEEP SECRET!)
                  </label>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                  >
                    {showPrivateKey ? '🙈 Hide' : '👁️ Show'}
                  </button>
                </div>
                <textarea
                  className="input"
                  value={showPrivateKey ? privateKey : '•••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'}
                  readOnly
                  rows={showPrivateKey ? 8 : 2}
                  style={{ fontFamily: 'monospace', fontSize: '11px', background: '#2a0a0a', borderColor: '#DC2626' }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button 
                    className="btn btn-ghost" 
                    style={{ fontSize: '12px' }}
                    onClick={() => copyToClipboard(privateKey)}
                  >
                    📋 Copy Private Key
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    style={{ fontSize: '12px' }}
                    onClick={downloadPrivateKey}
                  >
                    💾 Download Private Key
                  </button>
                </div>
                <p style={{ fontSize: '12px', color: '#DC2626', marginTop: '8px' }}>
                  ⚠️ WARNING: Save this private key now! If you lose it, your encrypted emails cannot be decrypted.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveGeneratedKey}
                  disabled={loading}
                >
                  {loading ? '⏳ Saving...' : '💾 Save Key to Account'}
                </button>
                <button 
                  className="btn btn-ghost"
                  onClick={() => {
                    setGeneratedKey(null);
                    setPublicKey('');
                    setPrivateKey('');
                  }}
                >
                  Start Over
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Key Form */}
      {showAddForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '20px' }}>📥 Import Existing PGP Key</h3>
          <form onSubmit={handleAddKey}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                Key Name
              </label>
              <input
                type="text"
                className="input"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g., Personal Key 2025"
                required
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                Public Key
              </label>
              <textarea
                className="input"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----"
                rows="6"
                required
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
                Private Key (optional, for signing)
              </label>
              <textarea
                className="input"
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                placeholder="-----BEGIN PGP PRIVATE KEY BLOCK-----"
                rows="6"
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '⏳ Adding...' : '💾 Save Key'}
            </button>
          </form>
        </div>
      )}

      {/* Key List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {keys.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
            <h3 style={{ marginBottom: '8px' }}>No PGP Keys</h3>
            <p style={{ color: 'var(--text-muted)' }}>
              Generate or import your first PGP key to start sending encrypted emails
            </p>
          </div>
        ) : (
          keys.map((key) => (
            <div key={key.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>{key.key_name}</h3>
                    {key.is_default && (
                      <span className="badge badge-primary">Default</span>
                    )}
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px', fontFamily: 'monospace' }}>
                    Fingerprint: {key.fingerprint}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!key.is_default && (
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '8px 12px', fontSize: '12px' }}
                      onClick={() => handleSetDefault(key.id)}
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    className="btn btn-ghost"
                    style={{ padding: '8px 12px', fontSize: '12px' }}
                    onClick={() => copyToClipboard(key.public_key)}
                  >
                    📋 Copy
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ padding: '8px 12px', fontSize: '12px' }}
                    onClick={() => handleDeleteKey(key.id)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div style={{ background: 'var(--darker)', padding: '12px', borderRadius: '8px', marginTop: '12px' }}>
                <code style={{ fontSize: '11px', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                  {key.public_key.substring(0, 100)}...{key.public_key.substring(key.public_key.length - 50)}
                </code>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PGPManager;

